import { useCallback, useEffect, useState } from "react";
import { usePlaidLink } from "react-plaid-link";
import { Landmark, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * PlaidConnectButton — "Connect bank". Asks the server for a one-time Link
 * token, opens Plaid Link, then hands the public token back to the server
 * (which exchanges + stores the access token; the browser never sees it).
 */
const PlaidConnectButton = ({ base, onConnected, className = "" }) => {
  const [token, setToken] = useState(null);
  const [busy, setBusy] = useState(false);

  const onSuccess = useCallback(async (public_token, metadata) => {
    setBusy(true);
    try {
      await api.post(`${base}/plaid/exchange`, { public_token, institution: metadata?.institution, accounts: metadata?.accounts || [] });
      toast.success(`${metadata?.institution?.name || "Bank"} connected`);
      onConnected?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not finish connecting the bank");
    } finally {
      setBusy(false);
      setToken(null);
    }
  }, [base, onConnected]);

  const { open, ready } = usePlaidLink({
    token,
    onSuccess,
    onExit: (err) => { setBusy(false); setToken(null); if (err) toast.error(err.display_message || "Bank connection cancelled"); },
  });

  useEffect(() => { if (token && ready) open(); }, [token, ready, open]);

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`${base}/plaid/link-token`);
      setToken(data.link_token);
    } catch (e) {
      setBusy(false);
      toast.error(e?.response?.data?.detail || "Could not start the bank connection");
    }
  };

  return (
    <button type="button" onClick={start} disabled={busy} data-testid="plaid-connect-btn" className={`inline-flex items-center gap-1.5 rounded-md bg-[#1a3fd1] px-3 py-2 text-xs font-semibold text-white hover:bg-[#1a3fd1]/85 disabled:opacity-60 ${className}`}>
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Landmark className="h-3.5 w-3.5" />} Connect bank
    </button>
  );
};

export default PlaidConnectButton;
